
import { Route } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import RoleBasedRoute from '@/components/general/RoleBasedRoute';
import DashboardSkeleton from '@/components/skeletons/DashboardSkeleton';
import BackofficeLayout from '@/components/backoffice/BackofficeLayout';
import MobileAppLayout from '@/components/mobile/MobileAppLayout';
import { isNativeApp } from '@/utils/platformUtils';

// Lazy load backoffice pages
const Dashboard = lazy(() => import('@/pages/backoffice/dashboard/Dashboard'));
const InvestmentAccounts = lazy(() => import('@/pages/backoffice/accounts/InvestmentAccounts'));
const AddAccount = lazy(() => import('@/pages/backoffice/accounts/AddAccount'));

// Profile pages
const TheClubUserProfile = lazy(() => import('@/pages/backoffice/profile/TheClubUserProfile'));
const PersonalInformation = lazy(() => import('@/pages/backoffice/profile/PersonalInformation'));
const PaymentMethods = lazy(() => import('@/pages/backoffice/profile/PaymentMethods'));
const MyReferralLink = lazy(() => import('@/pages/backoffice/profile/MyReferralLink'));

// Community pages
const MyCommunityGenealogyTree = lazy(() => import('@/pages/backoffice/community/MyCommunityGenealogyTree'));
const MyCommunityMembers = lazy(() => import('@/pages/backoffice/community/MyCommunityMembers'));

// Profits pages
const MyProfitsAccountsSummary = lazy(() => import('@/pages/backoffice/profits/MyProfitsAccountsSummary'));
const MyProfitsAccountsHistory = lazy(() => import('@/pages/backoffice/profits/MyProfitsAccountsHistory'));
const MyProfitsCommissionsSummary = lazy(() => import('@/pages/backoffice/profits/MyProfitsCommissionsSummary'));
const MyProfitsCommissionsHistory = lazy(() => import('@/pages/backoffice/profits/MyProfitsCommissionsHistory'));
const MyProfitsCommissionsPaymentHistory = lazy(() => import('@/pages/backoffice/profits/MyProfitsCommissionsPaymentHistory'));

// Adaptive layout wrapper — picks MobileAppLayout inside Capacitor, BackofficeLayout on web
const AdaptiveLayout = ({ children }: { children: React.ReactNode }) => {
  if (isNativeApp()) {
    return <MobileAppLayout>{children}</MobileAppLayout>;
  }
  return <BackofficeLayout>{children}</BackofficeLayout>;
};

// Helper function to create protected routes with and without language prefix
const createProtectedRouteWithLang = (path: string, element: JSX.Element) => [
  <Route key={path} path={path} element={
    <RoleBasedRoute requiredRoles={['user']}>
      <AdaptiveLayout>
        <Suspense fallback={<DashboardSkeleton />}>
          {element}
        </Suspense>
      </AdaptiveLayout>
    </RoleBasedRoute>
  } />,
  <Route key={`lang-${path}`} path={`/:lang${path}`} element={
    <RoleBasedRoute requiredRoles={['user']}>
      <AdaptiveLayout>
        <Suspense fallback={<DashboardSkeleton />}>
          {element}
        </Suspense>
      </AdaptiveLayout>
    </RoleBasedRoute>
  } />
];

export const getBackofficeRoutes = () => {
  return [
    // Dashboard
    ...createProtectedRouteWithLang('/dashboard', <Dashboard />),

    // Investment Accounts
    ...createProtectedRouteWithLang('/investment-accounts', <InvestmentAccounts />),
    ...createProtectedRouteWithLang('/add-account', <AddAccount />),

    // Profile Routes
    ...createProtectedRouteWithLang('/profile/the-club-user-profile', <TheClubUserProfile />),
    ...createProtectedRouteWithLang('/profile/personal-info', <PersonalInformation />),
    ...createProtectedRouteWithLang('/profile/payment-methods', <PaymentMethods />),
    ...createProtectedRouteWithLang('/profile/referral-link', <MyReferralLink />),

    // Community Routes
    ...createProtectedRouteWithLang('/my-community/genealogy-tree', <MyCommunityGenealogyTree />),
    ...createProtectedRouteWithLang('/my-community/members', <MyCommunityMembers />),

    // Profits Routes
    ...createProtectedRouteWithLang('/my-profits/accounts-summary', <MyProfitsAccountsSummary />),
    ...createProtectedRouteWithLang('/my-profits/accounts-history', <MyProfitsAccountsHistory />),
    ...createProtectedRouteWithLang('/my-profits/commissions-summary', <MyProfitsCommissionsSummary />),
    ...createProtectedRouteWithLang('/my-profits/commissions-history', <MyProfitsCommissionsHistory />),
    ...createProtectedRouteWithLang('/my-profits/commissions-payment-history', <MyProfitsCommissionsPaymentHistory />)
  ];
};
